import {
  Results,
  ResultsRecord,
  ConversionFactors,
  ProductOrConversionFactors,
  QuantityUnit,
  ConversionFactorGroupKey,
  ConversionFactorUnit,
} from '../models/unit.model';
import {
  Baseline,
  IElement,
  IElementID,
  IProductElement,
  IStoreyValuesWithElevation,
  OneOfElements,
  Project,
  ProjectMarketEstimates,
} from '../models/project.model';
import {
  ElementPath,
  filterElements,
  flattenElements,
  forEachElement,
  getAllBuildingVersions,
  getAllProductElements,
  getChildElements,
  isElement,
  isParentElement,
  isProductElement,
} from './recursive-element.helpers';
import { IProduct, ProductID } from '../models/product.model';
import {
  convertConversionFactors,
  multiplyConversionFactors,
} from './conversion.helpers';
import { cloneDeep, mapValues } from 'lodash';
import { cacheFactory, required } from './function.helpers';
import { CALCULATIONS } from '../constants/calculations.constants';
import { isNumberObjectsCloseTo, roundToDecimals } from './math.helpers';
import { ElementPropertyName } from '../models/element-property.model';
import { inputStringToNumber } from './string.helpers';
import {
  getElementName,
  isDeactivated,
  isDeleted,
  isTemporaryElementId,
} from './element.helpers';
import { TMP_ELEMENT_ID } from '../constants';
import {
  Co2eInputUnits,
  compressConversionFactors,
  getConversionFactorValue,
  mergeConversionFactors,
  sumConversionFactors,
  SupportedConversionUnits,
} from './conversion-factors.helpers';
import { getBuildingWidth } from './geometry/coordinates.helpers';
import { ItemOrItemId } from '../models/type-helpers.model';
import { IProposal } from '../models/proposals.model';
import { getElementsInProposal, getProposalById } from './proposal.helpers';
import {
  EMPTY_FROZEN_OBJECT,
  getId,
  omit,
  omitUndefined,
} from './object.helpers';
import {
  getCount,
  getElementProperty,
  getElementPropertyResolvedCountByNameOrId,
  hasCount,
} from './element-property.helpers';
import { getProjectGfa } from './project.helpers';
import { mapResultsByC1 } from './results/c-phase/c1.helpers';
import { mapResultsByC2 } from './results/c-phase/c2.helpers';
import { mapResultsByC3C4 } from './results/c-phase/c3-c4.helpers';
import {
  MapResultsContext,
  MapResultsContextWithGfa,
} from './results/map-results-context.model';
import { mapResultsByMaintenance } from './results/maintenance.helpers';
import { mapResultsByLifetime } from './results/lifetime.helpers';
import { mapResultsByContentCoverage } from './results/content-coverage.helpers';
import { mapResultsByElementCategories } from './results/element-categories.helpers';
import { ProductRecordOwners } from './product.helpers';

// Re-exported from the extracted results/ mapper modules to preserve the public
// API for existing importers (DEV-3321).
export { getGenericProductCharacteristics } from './results/generic-product-characteristics.helpers';
export { getMaintenanceActionCount } from './results/maintenance.helpers';
import {
  getGenericProductId,
  getProductElementConversionFactors,
} from './product-element.helpers';
import {
  getStoreyBuildingValues,
  getStoreysWithFallbacks,
  type LegacyStoreyMetadata,
} from './storeys.helpers';
import { printResultsDiff } from './debug-element.helpers';
import { EnergyProductId } from '@shared/templates/categories/energy/energy.model';
import { HeaterProducts } from '@shared/templates/categories/heating/heating.model';
import { getProposal } from './proposals';

const isProductConversionFactors = (
  product?: ProductOrConversionFactors,
): product is Pick<IProduct, 'conversion_factors'> =>
  !!product && 'conversion_factors' in product;

/**
 * Get results and fallback to empty results object if not found
 * @param record
 * @param id
 * @returns
 */
export const getResultsById = (
  record: ResultsRecord,
  id: string | undefined,
): Results => (id && record?.[id]) || {};

/**
 * Get a specific quantity from a ConversionFactorsRecord and fallback to 0 if not found
 * @param record
 * @param id
 * @param unit
 * @returns
 */
export const getValueFromResultsRecord = (
  record: ResultsRecord,
  id: IElementID | undefined,
  ...units: (ConversionFactorUnit | ConversionFactorGroupKey)[]
): number => {
  return getConversionFactorValue(getResultsById(record, id), ...units);
};

/**
 * Get conversion factors from a product or a conversion factors object
 * @param product
 * @returns
 */
export const getConversionFactors = (
  productOrFactors?: ProductOrConversionFactors,
): Results => {
  // If no product or conversion factors are provided, return empty conversion factors
  if (!productOrFactors) {
    return {};
  }

  return isProductConversionFactors(productOrFactors)
    ? productOrFactors.conversion_factors
    : productOrFactors;
};

/**
 * Get the relative conversion factors for a product.
 * Need to be multiplied with how many units of the product is used to get Results.
 * @param version Version should not be used when when calc
 * @param product
 * @returns
 */
export function getProductConversionFactors(
  product: IProduct | undefined,
  perUnit?: SupportedConversionUnits,
): Results | Partial<Results> {
  // TODO: This happens but should it be allowed?
  if (!product) {
    return {};
  }

  const conversionFactors = getConversionFactors(product);

  // Change the scale of conversion factors relative to another unit
  if (perUnit && perUnit !== product.unit) {
    return convertConversionFactors(conversionFactors, perUnit);
  }

  return conversionFactors;
}

/**
 * Get the combined conversion factors for an epd with a generic product as base (both optional).
 * @param epd
 * @param genericProduct
 * @param perUnit
 * @returns
 */
export const getCombinedConversionFactors = (
  epd: IProduct | undefined,
  genericProduct?: IProduct,
  perUnit?: SupportedConversionUnits,
) => {
  const epdFactors = getProductConversionFactors(epd);
  const genericFactors = getProductConversionFactors(genericProduct);

  return convertConversionFactors(
    mergeConversionFactors(genericFactors, epdFactors),
    perUnit,
  );
};

/** Value used by SCB, calculating 40km on a smaller truck. */
const LOCAL_TRUCK_EMISSION = 0.0001125;
const LONG_WAY_TRUCK_EMISSION = 0.000075;
const LOCAL_TRANSPORT_KM = 40;
const SMALL_TRUCK_EMISSION = 40 * LOCAL_TRUCK_EMISSION;

/**
 * Get A4 in KM for a product
 * @param product
 * @returns
 */
export const getProductTransportValue = (
  productOrConversionFactors: ProductOrConversionFactors,
): number => {
  const { co2e_A4, kg } = getConversionFactors(productOrConversionFactors);

  // No transport value if we can't translate unit to kg
  return kg ? a4ToKm(co2e_A4, kg) : 0;
};

/**
 * Get a factor between 0 and 1 that represents the amount of waste generated by the product.
 * A5 = (A1toA3 + A4) * waste_factor
 * @param product
 * @returns
 */
export const getProductWasteFactor = (factors: ConversionFactors): number => {
  const {
    'co2e_A1-A3': a1_a3 = 0,
    co2e_A4: a4 = 0,
    co2e_A5: a5 = 0,
  } = getConversionFactors(factors);
  const denominator = a1_a3 + a4;

  // Avoid division by zero (resulting in NaN)
  return denominator !== 0 ? a5 / denominator : 0;
};

/**
 * Convert a user input value to a conversion factor value.
 * Handles the cases for A4 and A5 in which the user input km or a percentage.
 * @param inputValue
 * @param factors
 * @param factor
 * @returns
 */
export const inputToConversionFactorValue = (
  inputValue: string | number | undefined | null,
  factors: ConversionFactors,
  factor: QuantityUnit,
  fallbacks?: ConversionFactors,
): number => {
  if (!inputValue) {
    return 0;
  }

  switch (factor) {
    case 'co2e_A4':
      return kmToA4(Number(inputValue), getConversionFactors(factors).kg ?? 0);
    case 'co2e_A5':
      return getA5FromWasteFactorPercentage(factors, inputValue, fallbacks);
    default:
      return inputStringToNumber(inputValue);
  }
};

/**
 * Get conversion factors from user input values.
 * co2e_A4 in km is converted to a kgCO2e/[unit] value
 * co2e_A5 in a percentage is converted to a a kgCO2e/[unit] value
 * @param conversion_factors
 * @returns
 */
export const fromTransportWasteConversionFactors = (
  conversion_factors: ConversionFactors,
  fallbacks?: ConversionFactors,
): ConversionFactors => {
  const factors = omitUndefined(conversion_factors);

  const co2e_A4 =
    factors.co2e_A4 !== undefined
      ? inputToConversionFactorValue(factors.co2e_A4, factors, 'co2e_A4')
      : undefined;

  // A4 must be set before A5 since A5 depends on "transport"
  const co2e_A5 =
    conversion_factors.co2e_A5 !== undefined
      ? inputToConversionFactorValue(
          factors.co2e_A5,
          { ...factors, co2e_A4 },
          'co2e_A5',
          fallbacks,
        )
      : undefined;

  return { ...factors, co2e_A4, co2e_A5 };
};

/**
 * Get conversion factors where A4 and A5 are converted to user input values.
 * co2e_A4 is converted to km (transport)
 * co2e_A5 is converted to a percentage (waste)
 * @param conversion_factors
 * @returns
 */
export const toTransportWasteConversionFactors = (
  conversion_factors: ConversionFactors,
): ConversionFactors => {
  const co2e_A4 =
    conversion_factors.co2e_A4 !== undefined
      ? getProductConversionFactorInputValue(conversion_factors, 'co2e_A4')
      : undefined;

  const co2e_A5 =
    conversion_factors.co2e_A5 !== undefined
      ? getProductConversionFactorInputValue(conversion_factors, 'co2e_A5')
      : undefined;

  return { ...conversion_factors, co2e_A4, co2e_A5 };
};

export const getConversionFactorUnit = (
  factor: QuantityUnit | Co2eInputUnits,
  product: IProduct,
): string => {
  switch (factor) {
    case 'co2e_A1-A3':
      return 'kgCO2e';
    case 'co2e_A4':
    case 'co2e_transport':
      return 'km';
    case 'co2e_A5':
    case 'co2e_waste':
    case 'co2e_waste_percent':
      return '%';
    case 'sek_A1-A3':
      return `kr/${product.unit}`;
    default:
      return 'kg';
  }
};

/**
 *
 * @param product
 * @param wasteFactorInput A percentage between 0 and 100 OR 100 and 200;
 * @returns
 */
export const getA5FromWasteFactorPercentage = (
  factors: ConversionFactors,
  wasteFactorInput: number | string,
  fallbacks?: ConversionFactors,
): number => {
  let wasteFactor = inputStringToNumber(wasteFactorInput) / 100;

  // Boverket uses waste factor as 1.12 for 12% waste
  if (wasteFactor > 1) {
    wasteFactor -= 1;
  }

  const { 'co2e_A1-A3': a1_a3, co2e_A4: a4 } = {
    ...fallbacks,
    ...omitUndefined(factors),
  };

  if (wasteFactor < 0 || wasteFactor > 1) {
    console.error('Waste factor must be between 0 and 100%');
  }

  if (a1_a3 && a4) return (a1_a3 + a4) * wasteFactor;
  if (a1_a3) return a1_a3 * wasteFactor;
  if (a4) return a4 * wasteFactor;
  return wasteFactor;
};

/**
 * Get the editable value for a product. For transport (A4) it will be in km, for waste (A5) it will be a factor between 0 and 100%
 * @param product
 * @param factor
 */
export const getProductConversionFactorInputValue = (
  factors: ConversionFactors,
  factor: keyof Results,
): number => {
  const conversionFactors = getConversionFactors(factors);

  if (factor === 'co2e_A4') {
    return getProductTransportValue(conversionFactors);
  }
  if (factor === 'co2e_A5') {
    return roundToDecimals(getProductWasteFactor(conversionFactors) * 100);
  }
  return conversionFactors[factor] ?? 0;
};

/**
 * Get A4 values (in kg co2e / [unit])
 * Page 146: https://www.boverket.se/contentassets/5c704bbb2b2f4bd1a31beecf355efaa4/referensvarden-for-klimatpaverkan-vid-uppforande-av-byggnader_kth-2021.pdf
 */
export const kmToA4 = (km: number, kgPerUnit = 1): number => {
  return (
    (km < LOCAL_TRANSPORT_KM
      ? km * LOCAL_TRUCK_EMISSION
      : SMALL_TRUCK_EMISSION +
        (km - LOCAL_TRANSPORT_KM) * LONG_WAY_TRUCK_EMISSION) * kgPerUnit
  );
};

/** Returns the distance traveled for a given kgCO2e. */
export const a4ToKm = (a4: number | undefined, kgPerUnit = 1): number => {
  if (!a4 || !kgPerUnit) {
    return 0;
  }

  // A4 per other unit like m3
  const a4PerUnit = a4 / kgPerUnit;

  if (a4PerUnit > SMALL_TRUCK_EMISSION) {
    return Math.round(
      LOCAL_TRANSPORT_KM +
        (a4PerUnit - SMALL_TRUCK_EMISSION) / LONG_WAY_TRUCK_EMISSION,
    );
  }
  return Math.round(a4PerUnit / LOCAL_TRUCK_EMISSION);
};

/**
 * Get conversion factors, including global warming potential, for a ProductElement.
 * Note that this values are relative to how much the product is used (parentElement.count).
 * @param version Version to try to grab products from.
 * @param element
 * @param product Calculating a recipe will require the product to be passed in.
 * @returns
 */
const sumProductElementConversionFactors = (
  productRecordOwner: ProductRecordOwners,
  element: IProductElement,
  product?: IProduct,
  marketEstimates?: ProjectMarketEstimates,
): Results => {
  const conversionFactors = getProductElementConversionFactors(
    productRecordOwner,
    element,
    product,
    marketEstimates,
  );
  const productCount = getCount(element, true).resolved;

  // Sum is the ConversionFactors * productCount
  return multiplyConversionFactors(conversionFactors, productCount);
};

/**
 * Remove stale rows for the local EPD-picker element from a {@link ResultsRecord}.
 */
export const omitTemporaryElementFromResultsRecord = (
  record: ResultsRecord,
): ResultsRecord => {
  if (!(TMP_ELEMENT_ID in record)) return record;
  const { [TMP_ELEMENT_ID]: _removed, ...rest } = record;
  return rest;
};

/**
 * Get results from each ProductElement in the version.
 * This will later be used to sum up the results for each parent element.
 * @param version
 * @returns
 */
export const generateProductElementResultsRecord = (
  project: Project,
  version: Baseline,
) => {
  // Cache this calculation by version id and version
  return cacheFactory(
    () => {
      const record: ResultsRecord = {};

      forEachElement(version, (element, path) => {
        if (isTemporaryElementId(element.id)) return;
        if (isProductElement(element)) {
          record[element.id] = mapResults(
            generateElementConversionFactorSum(
              path,
              element,
              undefined,
              project.marketEstimates,
            ),
            { project, path, productElement: element },
          );
        }
      });

      return record;
    },
    `getProductElementResultsRecord[${version.id}]`,
    [version],
  );
};

/**
 * Adjust results based on ALL mapper methods in the order they are defined.
 * Legacy entry point used by {@link generateProductElementResultsRecord}.
 * New code should use {@link mapProductElementResults} + the parent-level
 * category/coverage logic in `build-results-record.helpers.ts` instead.
 *
 * @param results - Raw conversion factor results
 * @param context - Project, path, productElement (categoryElement is added by mappers)
 * @returns Mapped results (e.g. AHU energy A→B6, lifetime B4)
 */
export const mapResults = (
  results: Results,
  context: MapResultsContext,
): Results => {
  const contextWithGfa: MapResultsContextWithGfa = {
    ...context,
    gfa: getProjectGfa(context.project),
  };
  const afterCategories = mapResultsByElementCategories(
    results,
    contextWithGfa,
  );
  return compressConversionFactors(
    mapResultsByCPhase(
      mapResultsByContentCoverage(
        mapResultsByLifetime(
          mapResultsByMaintenance(afterCategories, context, results),
          context,
        ),
        context,
      ),
      contextWithGfa,
    ),
  );
};

/**
 * Per-product C-phase (end-of-life) mappers in module order: C1 demolition
 * (material/crane diesel), C2 transport, C3-C4 actual waste processing. Each reads the
 * product's mass (`results.kg`) and is linear in mass so parents simply sum. The
 * GFA-based C1 base energy and the C3-C4 BD7/BD8 schablon are building-wide and added
 * once at the version level (see `build-results-record.helpers.ts`).
 */
const mapResultsByCPhase = (
  results: Results,
  context: MapResultsContextWithGfa,
): Results =>
  mapResultsByC3C4(mapResultsByC2(mapResultsByC1(results, context)), context);

/**
 * Per-product mapper: applies all transformations that belong on a leaf product
 * element row — currently the owning ancestors' category mappers (e.g. AHU/Heating
 * A→B6) followed by {@link mapResultsByMaintenance} (B2) and
 * {@link mapResultsByLifetime} (B4). Used at the leaf level in
 * the bottom-up rollup so the product row already shows the category-adjusted
 * value, and parents simply sum.
 *
 * Order matches legacy {@link mapResults}: categories run first so the
 * lifetime step sees the category-adjusted A-stage values.
 * {@link ElementPropertyName.ContentCoverage} is intentionally NOT applied here —
 * it's applied at the owning element's rollup via {@link applyOwnContentCoverage}.
 *
 * @param results - Raw conversion factor results for a single product element
 * @param context - Project, element path (version → product), and the target product
 * @returns Mapped results with categories + lifetime applied, compressed
 */
export const mapProductElementResults = (
  results: Results,
  context: MapResultsContext,
): Results => {
  const contextWithGfa: MapResultsContextWithGfa = {
    ...context,
    gfa: getProjectGfa(context.project),
  };
  return compressConversionFactors(
    mapResultsByCPhase(
      mapResultsByLifetime(
        mapResultsByMaintenance(
          mapResultsByElementCategories(results, contextWithGfa),
          context,
          results,
        ),
        context,
      ),
      contextWithGfa,
    ),
  );
};

/**
 * Apply {@link ElementPropertyName.ContentCoverage} defined directly on `element`.
 * Unlike the legacy {@link mapResultsByContentCoverage} which resolves coverage along
 * the full ancestor path, this checks only the element itself — preventing
 * double-application when rolling up through multiple levels.
 *
 * On {@link IElement}, only an explicit `count` (user-set slider) triggers scaling.
 * Category-mandatory hidden properties that only define `fallbackCount` are ignored
 * so version-level coverage ({@link Baseline}) is not applied again on every parent rollup.
 *
 * @param results - Summed children results to scale
 * @param element - The element to read ContentCoverage from (not ancestors)
 * @returns Scaled results (1/coverage) if coverage ∈ (0,1), otherwise unchanged
 */
export const applyOwnContentCoverage = (
  results: Results,
  element: IElement | Baseline,
): Results => {
  const property = getElementProperty(
    element,
    ElementPropertyName.ContentCoverage,
  );
  if (!property) return results;

  // Mandatory category template on elements: fallbackCount only — never scale until user sets count.
  if (isElement(element) && !hasCount(property)) return results;

  const coverage = getElementPropertyResolvedCountByNameOrId(
    element,
    ElementPropertyName.ContentCoverage,
    1,
  );
  if (!coverage || coverage === 1) return results;
  return multiplyConversionFactors(results, 1 / coverage);
};

/**
 * Generate a new record of all results for each element in version.
 * Only use for enricher or fallback since it's heavy to calculate.
 *  { [id]: results, ... }
 * @param project
 * @param version
 * @param proposalId Proposals might have different element lists, provide a key to cache the results separately.
 */
export const generateResultsRecord = (
  project: Project,
  version: Baseline,
  proposalId?: string,
): ResultsRecord => {
  // Cache this calculation by version id and version
  return cacheFactory(
    () => {
      const proposal = proposalId
        ? getProposalById(version, proposalId)
        : undefined;

      // If a proposal id is provided, use the elements active in the proposal else use all elements
      const elements = proposal
        ? getElementsInProposal(version, proposal)
        : flattenElements(version);

      // Create an editable record of all results for each element in version
      const record: ResultsRecord = cloneDeep(
        generateProductElementResultsRecord(project, version),
      );

      // No need to calculate product elements again
      const parentElements = elements.filter(isParentElement);

      // Sum all productElements in each element
      parentElements.forEach((element) => {
        // All children that are active as a flat list (will also remove all children of deactivated elements)
        const children = filterElements(
          getChildElements(element),
          (child) => {
            // Exclude deactivated elements
            if (isDeactivated(child, false) || isDeleted(child)) {
              return false;
            }
            // Always include product elements (as long as their parents are included)
            if (isProductElement(child)) {
              return true;
            }

            // Don't allow two of the same ElementVersion to be included
            // Only include other types of elements if they are provided in the elements array
            return required(elements).includes(child);
          },
          false,
        );

        // Element results is the sum of all products in it
        const productFactors = children
          .filter(isProductElement)
          .map((p) => record[p.id]);

        record[element.id] = sumConversionFactors(...productFactors);
      });

      return omitTemporaryElementFromResultsRecord(record);
    },
    `generateResultsRecord[${version.id}][${proposalId ?? 'all'}]`,
    [version],
  );
};

export const getProjectResultsRecord = (project: Project): ResultsRecord => {
  return cacheFactory(
    () => {
      const versions = getAllBuildingVersions(project);
      const factors = versions.map((version) => getResultsRecord(version));
      return factors.reduce((acc, curr) => ({ ...acc, ...curr }), {});
    },
    `getProjectResultsRecord[${project.id}]`,
    [project],
  );
};

/**
 * Get a record of all results for each element in a proposal.
 * @param project
 * @param version Version to get the results record for.
 * @param inProposal Optional proposal to get the results record for. If not provided, the active proposal record is used
 * @returns
 */
export const getResultsRecord = (
  version: Baseline,
  inProposal?: ItemOrItemId<IProposal>,
): ResultsRecord => {
  const id = inProposal ? getId(inProposal) : undefined;
  const proposal = id ? getProposal(version, id) : undefined;
  return (
    proposal?.resultsRecord ?? version.resultsRecord ?? EMPTY_FROZEN_OBJECT
  );
};

/**
 * Get results for a specific element.
 * Won't recalculate the results if they already exist.
 * @param version
 * @param element
 * @param inProposal Pass the element co2, cost when used in a certain proposal
 * @returns
 */
export const getElementResults = (
  version: Baseline,
  elementOrId: ItemOrItemId<OneOfElements> | undefined,
  inProposal?: ItemOrItemId<IProposal>,
): Results => {
  const id = elementOrId ? getId(elementOrId) : undefined;
  const record = getResultsRecord(version, inProposal);

  return id && record[id] ? record[id] : EMPTY_FROZEN_OBJECT;
};

/**
 * Get results for an element by id from a project (active proposal's {@link getResultsRecord},
 * else {@link Baseline.resultsRecord}). Use instead of {@link IElement.results} when
 * comparing migrations — persisted totals live on the record, not on each element.
 *
 * @param project - Project containing building versions
 * @param elementId - Element id to resolve
 * @returns Results row for that id, or empty object if missing
 */
export const getElementResultsFromProject = (
  project: Project,
  elementId: string,
): Results => {
  for (const version of getAllBuildingVersions(project)) {
    if (flattenElements(version).some((el) => el.id === elementId)) {
      return getResultsRecord(version)[elementId] ?? EMPTY_FROZEN_OBJECT;
    }
  }
  return {};
};

/**
 * Get latest conversion factors totals for a specific element
 * IMPORTANT: Only use this if you need to recalculate the conversion factors for a specific element only.
 * All other cases use getResultsRecord above
 * @param version
 * @param element
 * @param marketEstimates - Project-level market estimates for percentile-based overrides
 * @returns
 */
const sumElementConversionFactors = (
  version: Baseline | ElementPath,
  element: IElement,
  marketEstimates?: ProjectMarketEstimates,
): Results => {
  // We use this to calculate possible recipes so cache it by element id + recipe id
  return cacheFactory(
    (): Results => {
      if (isDeactivated(element)) {
        return {};
      }
      const factors: Results[] = [];

      forEachElement(element, (el, path) => {
        // Only sum product elements with no inactive parents
        if (isProductElement(el) && !path.some((e) => isDeactivated(e))) {
          factors.push(
            sumProductElementConversionFactors(
              version,
              el,
              undefined,
              marketEstimates,
            ),
          );
        }
      });

      return sumConversionFactors(...factors);
    },
    `sumElementConversionFactors[${element.id}, ${element.recipe_id}]`,
    [element], // Only element is needed to cache this correctly
  );
};

/**
 * Get summary of conversion factors for a specific element or product element.
 * @param versionOrPath The version OR path (with version as first element) need to calculate counts (and for products)
 * @param element Which element to get the conversion factors sum for
 * @param product Optional product. Needed for recipes
 * @param marketEstimates - Project-level market estimates for percentile-based overrides
 * @returns A record containing how much of each unit the element contains (kg, m³, co2e_A1-A3 etc)
 */
export const generateElementConversionFactorSum = (
  versionOrPath: Baseline | ElementPath,
  element: IElement | IProductElement, // TODO: Make this support versions
  product?: IProduct, // Needed for recipes?
  marketEstimates?: ProjectMarketEstimates,
): Results => {
  if (isElement(element)) {
    return sumElementConversionFactors(versionOrPath, element, marketEstimates);
  } else if (isProductElement(element)) {
    return sumProductElementConversionFactors(
      versionOrPath,
      element,
      product,
      marketEstimates,
    );
  }
  return {};
};

/**
 * Get summary of all conversion factors in an element or product element in a specific unit.
 * Return 0 if the unit is not found.
 * @param version
 * @param element
 * @param unit
 * @returns
 */
export const getElementSumInUnit = (
  version: Baseline,
  element: IElement,
  unit: SupportedConversionUnits,
): number =>
  getConversionFactorValue(
    generateElementConversionFactorSum(version, element),
    unit,
  );

/**
 * Get mass of element.
 * Which is the sum of all productElement mass in the element.
 * @param version
 * @param element
 * @returns
 */
export const getElementMass = (version: Baseline, element: IElement) =>
  getElementSumInUnit(version, element, 'kg');

/**
 * Get total amount of CO2e of an element.
 * @param version
 * @param element
 * @returns
 */
export const getElementCO2e = (version: Baseline, element: IElement) =>
  getElementSumInUnit(version, element, 'co2e');

/**
 * Get volume of element.
 * Which is the sum of all productElement volumes in the element.
 * @param version
 * @param element
 * @returns
 */
export const getElementVolume = (version: Baseline, element: IElement) =>
  getElementSumInUnit(version, element, 'm³');

export const getCO2eRecord = (
  factorRecord: ResultsRecord,
  elementQuantity = 1,
): Record<IElementID, number> => {
  return mapValues(
    factorRecord,
    (factors) => getConversionFactorValue(factors, 'co2e') * elementQuantity,
  );
};
type StoreyVariables = {
  total_height: number;
  total_height_above_ground: number;
  total_height_below_ground: number;
  gfa_above_ground: number;
  gfa_below_ground: number;
  external_gwa: number;
  external_gwa_above_ground: number;
  external_gwa_below_ground: number;
  gross_volume: number;
  gross_volume_above_ground: number;
  gross_volume_below_ground: number;
  gfa_ground_floor_slabs: number;
  gfa_cantilevered_floor_slabs: number;
  gfa_terrace_slabs: number;
  gfa_sky: number;
  gfa_activities: number;
};

/**
 * Calculate the width under a saddle roof where the room height is minHeight or more.
 * @param building_width
 * @param minHeight
 * @param roofPitch
 * @returns
 */
const getWidthOfMinimumRoomHeight = (
  building_width: number,
  innerHeight: number,
  roofPitch: number,
): number => {
  const minHeight = 1.9 - innerHeight;

  if (!roofPitch || minHeight <= 0) {
    return building_width;
  }

  // +60cm after the point where height is 1.9m height is counted as width
  const minWidth =
    0.6 * 2 +
    building_width -
    (2 * minHeight) / Math.tan((roofPitch * Math.PI) / 180);

  // width can't be larger than building width.
  return Math.min(building_width, minWidth);
};

/**
 * Get the building length based on the area and span
 * Note that this length is an estimate of length if we recalculate the shape to a rectangle.
 * @param footprintArea
 * @param buildingWidth
 */
export const getBuildingLengthEstimate = (
  footprintArea: number,
  buildingWidth: number,
): number => (buildingWidth ? footprintArea / buildingWidth : 0);

/**
 * Get available GFA for Activities on a Storey.
 * @param gfa_sky The area of this storey that is open to the sky (that doesn't have another floor above it)
 * @param gfa The total gfa of this storey
 * @param building_width
 * @param innerHeight Height to the point where the roof starts
 * @param roof_pitch Angle in degrees
 * @returns
 */
const getActivitiesGFA = (
  gfa_sky: number,
  gfa: number,
  building_width: number,
  innerHeight: number,
  roof_pitch = 20,
) => {
  const length = getBuildingLengthEstimate(gfa, building_width);
  const lengthWithRoof = length * (gfa_sky / gfa);
  const lengthWithoutRoof = length - lengthWithRoof;

  const areaWithoutRoof = lengthWithoutRoof * building_width;
  const areaWithRoof =
    lengthWithRoof *
    getWidthOfMinimumRoomHeight(building_width, innerHeight, roof_pitch);

  return roundToDecimals(areaWithoutRoof + areaWithRoof);
};

export const getStoreyVariables = (
  storey: IStoreyValuesWithElevation,
  /** IMPORTANT: which index storey has in the list of meta.storeys */
  floorIndex: number,
  meta: LegacyStoreyMetadata,
): StoreyVariables => {
  if (storey === undefined) throw new Error('storey must be defined');
  const { gfa, inner_height, elevation } = storey;
  const { storeyArea, storeyPerimeter } = getStoreyBuildingValues(
    getStoreysWithFallbacks(meta),
    floorIndex,
  );

  const total_height = CALCULATIONS.total_height.calculate({
    inner_height,
  });
  const total_height_above_ground =
    CALCULATIONS.total_height_above_ground.calculate({
      elevation,
      total_height,
    });
  const total_height_below_ground =
    CALCULATIONS.total_height_below_ground.calculate({
      total_height,
      total_height_above_ground,
    });
  const gfa_above_ground = CALCULATIONS.gfa_above_ground.calculate({
    total_height_below_ground,
    gfa,
  });
  const gfa_below_ground = CALCULATIONS.gfa_below_ground.calculate({
    total_height_below_ground,
    gfa,
  });

  const external_gwa = CALCULATIONS.external_gwa.calculate({
    perimeter: storeyPerimeter,
    total_height,
  });

  const external_gwa_above_ground =
    CALCULATIONS.external_gwa_above_ground.calculate({
      perimeter: storeyPerimeter,
      total_height_above_ground,
    });
  const external_gwa_below_ground =
    CALCULATIONS.external_gwa_below_ground.calculate({
      perimeter: storeyPerimeter,
      total_height_below_ground,
    });
  const gross_volume = CALCULATIONS.gross_volume.calculate({
    gfa: storeyArea,
    total_height,
  });
  const gross_volume_above_ground =
    CALCULATIONS.gross_volume_above_ground.calculate({
      gfa: storeyArea,
      total_height_above_ground,
    });
  const gross_volume_below_ground =
    CALCULATIONS.gross_volume_below_ground.calculate({
      gfa: storeyArea,
      total_height_below_ground,
    });

  let gfa_ground_floor_slabs = CALCULATIONS.gfa_ground_floor_slabs.calculate();
  let gfa_cantilevered_floor_slabs =
    CALCULATIONS.gfa_cantilevered_floor_slabs.calculate();
  let gfa_terrace_slabs = CALCULATIONS.gfa_terrace_slabs.calculate();
  let gfa_sky = CALCULATIONS.gfa_sky.calculate();

  /** how much bigger storey area this storey has than the previous storey
   * Do not confuse with floor area. This is the area of the storey itself, i.e. around the building.
   *
   * If the previous storey has a mezzanine edge, the area increase is 0
   *
   * If the previous storey has no mezzanine edge, the area increase is the difference between the storey area and the previous storey area
   */
  const { storeyArea: prevStoreyArea } = getStoreyBuildingValues(
    getStoreysWithFallbacks(meta),
    floorIndex - 1,
  );
  const areaIncreaseFromBelow = storeyArea - prevStoreyArea;

  const { storeyArea: nextStoreyArea } = getStoreyBuildingValues(
    getStoreysWithFallbacks(meta),
    floorIndex + 1,
  );
  /** how much smaller storey area the next storey has than this storey */
  const areaDecreaseToAbove = storeyArea - nextStoreyArea;

  if (areaIncreaseFromBelow > 0) {
    if (elevation <= 0) {
      gfa_ground_floor_slabs = areaIncreaseFromBelow;
    } else {
      gfa_cantilevered_floor_slabs = areaIncreaseFromBelow;
    }
  }
  if (areaDecreaseToAbove > 0) {
    // Roof at or above grade is sky. A basement lid at elevation 0 is not buried.
    if (elevation + total_height < 0) {
      gfa_terrace_slabs = areaDecreaseToAbove;
    } else {
      gfa_sky = areaDecreaseToAbove;
    }
  }

  const building_width = getBuildingWidth(meta.building_footprint?.coordinates);
  const gfa_activities = getActivitiesGFA(
    gfa_sky,
    gfa,
    building_width,
    inner_height,
  );

  return {
    total_height,
    total_height_above_ground,
    total_height_below_ground,
    gfa_above_ground,
    gfa_below_ground,
    external_gwa,
    external_gwa_above_ground,
    external_gwa_below_ground,
    gross_volume,
    gross_volume_above_ground,
    gross_volume_below_ground,
    gfa_ground_floor_slabs,
    gfa_cantilevered_floor_slabs,
    gfa_terrace_slabs,
    gfa_sky,
    gfa_activities,
  };
};

// Temporary ignore EnergyProducts (KEEP, need this for two more migrations, then remove)
const PRODUCT_ID_IGNORE_LIST: ProductID[] = [
  ...Object.values(EnergyProductId),
  ...Object.values(HeaterProducts),
];

export type IsEqualResultsOptions = {
  excludedKeys?: (keyof Results)[];
  /** Tolerance in percent (0–100), e.g. 5 for 5%. Not a factor. */
  tolerancePercent?: number;
};

/**
 * Test if two results objects are equal.
 * @param a
 * @param b
 * @param options.excludedKeys Properties to exclude from the comparison
 * @param options.tolerancePercent When set, allow numeric values to differ by this percent (0–100, e.g. 5 for 5%). Not a factor.
 * @returns
 */
export const isEqualResults = (
  a: Results | undefined = {},
  b: Results | undefined = {},
  options?: IsEqualResultsOptions,
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const excludedKeys = options?.excludedKeys ?? [];
  const toleranceFactor =
    options?.tolerancePercent !== undefined
      ? options.tolerancePercent / 100
      : undefined;
  return isNumberObjectsCloseTo(
    omit(a, ...excludedKeys),
    omit(b, ...excludedKeys),
    toleranceFactor,
  );
};

/**
 * Checks if the results have changed in two projects
 * @param next
 * @param prev
 * @param deep If true, also compare all elements in building versions
 * @param tolerancePercent When set, allow numeric results to differ by this percent (0–100). Not a factor.
 * @param excludedKeys {@link Results} keys to exclude from comparison
 * @param logDiff When true, always print the diff when comparing, regardless of pass/fail
 */
export const isEqualResultsInProjects = (
  next: Project,
  prev: Project | undefined,
  deep = false,
  tolerancePercent?: number,
  excludedKeys: (keyof Results)[] = [],
  logDiff = false,
) => {
  if (!prev) {
    return false;
  }
  if (next === prev) {
    return true;
  }

  const toleranceOpt: IsEqualResultsOptions | undefined =
    tolerancePercent !== undefined || excludedKeys.length > 0
      ? { tolerancePercent, excludedKeys }
      : undefined;

  // Compare project results (isEqualResults applies omit via toleranceOpt.excludedKeys)
  const projectEqual = isEqualResults(next.results, prev.results, toleranceOpt);
  if (logDiff) {
    printResultsDiff(
      omit(next.results, ...excludedKeys),
      omit(prev.results, ...excludedKeys),
      `project "${next.name}" (${next.id})`,
    );
  }
  if (!projectEqual) return false;

  // Deep comparison should include all product elements (since they are the ones from which results are summed up)
  // If not deep we just check all building versions since project.results is just the result of the selected building version
  const getChildrenFn = deep ? getAllProductElements : getAllBuildingVersions;
  const nextElements = getChildrenFn(next);
  const prevElements = getChildrenFn(prev);

  // Compare element results to avoid
  for (const element of nextElements) {
    const prevElement = prevElements.find((v) => v.id === element.id);

    // Temporary ignore EnergyProducts since they are all changed (KEEP, need this for two more migrations, then remove)
    const productId = isProductElement(element)
      ? getGenericProductId(element)
      : undefined;

    if (productId && PRODUCT_ID_IGNORE_LIST.includes(productId)) {
      continue;
    }

    // TODO: Can be optimize probably but this is only used when migrating
    const nextElementResults = getElementResultsFromProject(next, element.id);
    const prevElementResults = prevElement
      ? getElementResultsFromProject(prev, prevElement.id)
      : {};

    const elementEqual = isEqualResults(
      nextElementResults,
      prevElementResults,
      toleranceOpt,
    );
    if (logDiff) {
      printResultsDiff(
        omit(nextElementResults, ...excludedKeys),
        omit(prevElementResults, ...excludedKeys),
        `Element ${getElementName(element)} (${element.id}, ${productId ?? 'no product'})`,
      );
    }
    if (!elementEqual) return false;
  }

  return true;
};

/**
 * Get results divided by a factor (like if you want results per kg)
 * @param results
 * @param denominator
 * @returns
 */
export const getResultsByFactor = <
  T extends Results | ConversionFactors | number,
>(
  results: T | undefined,
  denominator = 1,
): T => {
  const factor = denominator > 0 ? 1 / denominator : 0;

  if (typeof results === 'number') {
    return (results * factor) as T;
  }
  return multiplyConversionFactors(results, factor);
};
